import React, { useState, useEffect, useMemo } from 'react'
import {
  FileText,
  TrendingDown,
  TrendingUp,
  DollarSign,
  Calculator,
  PieChart,
  Download,
  AlertCircle,
  Info
} from 'lucide-react'
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts'
import { Button } from '../ui/Button'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { Tooltip as UITooltip } from '../ui/Tooltip'
import { useToastHelpers } from '../../contexts/ToastContext'
import {
  TaxAnalysisResult,
  TaxDeductions,
  UserTaxProfile
} from '../../types/tax-analysis'
import { PropertyResponse } from '../../services/api'

interface AnnualTaxSummaryProps {
  property: PropertyResponse
  taxProfile?: UserTaxProfile | null
  selectedYear?: string
  loading?: boolean
  onYearChange?: (year: string) => void
}

const EXPENSE_COLORS = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Green
  '#F59E0B', // Amber
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#F97316', // Orange
  '#6366F1'  // Indigo
]

const CURRENT_YEAR = new Date().getFullYear()
const AVAILABLE_YEARS = Array.from({ length: 5 }, (_, i) => (CURRENT_YEAR - 2 + i).toString())

export function AnnualTaxSummary({
  property,
  taxProfile,
  selectedYear = CURRENT_YEAR.toString(),
  loading = false,
  onYearChange
}: AnnualTaxSummaryProps) {
  const { showError, showSuccess } = useToastHelpers()
  const [taxAnalysisResult, setTaxAnalysisResult] = useState<TaxAnalysisResult | null>(null)
  const [isCalculating, setIsCalculating] = useState(false)

  // Calculate tax analysis when inputs change
  useEffect(() => {
    if (property && taxProfile) {
      calculateTaxAnalysis()
    }
  }, [property, taxProfile, selectedYear])

  const calculateTaxAnalysis = async () => {
    if (!property || !taxProfile) return

    setIsCalculating(true)
    try {
      // Calculate comprehensive tax analysis
      const analysis = await performTaxCalculations(property, taxProfile, selectedYear)
      setTaxAnalysisResult(analysis)
    } catch (error) {
      console.error('Tax calculation error:', error)
      showError('Calculation Error', 'Failed to calculate tax analysis. Please check your inputs.')
    } finally {
      setIsCalculating(false)
    }
  }

  const performTaxCalculations = async (
    prop: PropertyResponse,
    profile: UserTaxProfile,
    year: string
  ): Promise<TaxAnalysisResult> => {
    // Calculate basic property metrics
    const monthlyRent = prop.expected_monthly_rent || 0
    const annualRent = monthlyRent * 12

    // Calculate mortgage payment
    const monthlyMortgagePayment = calculateMonthlyPayment(
      prop.loan_amount,
      prop.interest_rate,
      prop.loan_term
    )
    const annualMortgageInterest = monthlyMortgagePayment * 12 * 0.8 // Approximate interest portion

    // Calculate depreciation
    const depreciableBasis = prop.purchase_price - (prop.purchase_price * 0.2) // Assume 20% land value
    const annualDepreciation = depreciableBasis / 27.5 // Residential property depreciation

    // Calculate total deductions
    const deductions: TaxDeductions = {
      property_id: prop.property_id || 'temp',
      year: year,
      mortgage_interest: annualMortgageInterest,
      property_taxes: prop.annual_taxes,
      insurance: prop.annual_insurance,
      repairs_maintenance: annualRent * 0.05, // Estimate 5% of rent
      professional_services: annualRent * 0.03, // Estimate 3% of rent
      depreciation: annualDepreciation,
      utilities: 0, // Assuming tenant pays
      advertising: annualRent * 0.01, // Estimate 1% of rent
      travel_expenses: 500, // Estimated annual travel
      office_expenses: 300, // Estimated office expenses
      other_expenses: [],
      total_deductions: 0
    }

    deductions.total_deductions = Object.values(deductions)
      .filter(val => typeof val === 'number')
      .reduce((sum, val) => sum + val, 0)

    // Calculate taxable income
    const netRentalIncomeBeforeDepreciation = annualRent - (deductions.total_deductions - deductions.depreciation)
    const taxableIncome = netRentalIncomeBeforeDepreciation - deductions.depreciation

    // Calculate tax savings
    const marginalTaxRate = profile.federal_tax_bracket + profile.state_tax_rate
    const taxSavings = deductions.total_deductions * marginalTaxRate
    const effectiveTaxRate = annualRent > 0 ? (taxSavings / annualRent) : 0

    // Calculate after-tax cash flow
    const beforeTaxCashFlow = annualRent - (monthlyMortgagePayment * 12) - prop.annual_taxes - prop.annual_insurance
    const afterTaxCashFlow = beforeTaxCashFlow + taxSavings

    return {
      property_id: prop.property_id || 'temp',
      analysis_year: year,
      rental_income: annualRent,
      total_deductions: deductions.total_deductions,
      net_rental_income: netRentalIncomeBeforeDepreciation,
      taxable_income: Math.max(taxableIncome, 0),
      tax_savings: taxSavings,
      effective_tax_rate: effectiveTaxRate,
      after_tax_cash_flow: afterTaxCashFlow,
      depreciation_recapture_liability: annualDepreciation * 0.25, // 25% depreciation recapture rate
      tax_profile_used: profile
    }
  }

  const deductionsData = useMemo(() => {
    if (!taxAnalysisResult) return []

    const analysis = taxAnalysisResult

    return [
      { category: 'Mortgage Interest', amount: analysis.rental_income * 0.4, percentage: 40 },
      { category: 'Depreciation', amount: analysis.rental_income * 0.25, percentage: 25 },
      { category: 'Property Taxes', amount: analysis.rental_income * 0.15, percentage: 15 },
      { category: 'Insurance', amount: analysis.rental_income * 0.08, percentage: 8 },
      { category: 'Repairs & Maintenance', amount: analysis.rental_income * 0.05, percentage: 5 },
      { category: 'Professional Services', amount: analysis.rental_income * 0.03, percentage: 3 },
      { category: 'Other Expenses', amount: analysis.rental_income * 0.04, percentage: 4 }
    ].filter(item => item.amount > 0)
  }, [taxAnalysisResult])

  const yearOverYearData = useMemo(() => {
    if (!taxAnalysisResult) return []

    // Generate sample year-over-year data
    return AVAILABLE_YEARS.map(year => ({
      year,
      rental_income: taxAnalysisResult.rental_income * (1 + Math.random() * 0.1 - 0.05),
      tax_savings: taxAnalysisResult.tax_savings * (1 + Math.random() * 0.15 - 0.075),
      after_tax_cash_flow: taxAnalysisResult.after_tax_cash_flow * (1 + Math.random() * 0.12 - 0.06)
    }))
  }, [taxAnalysisResult])

  const handleExportTaxSummary = () => {
    if (!taxAnalysisResult) {
      showError('Export Error', 'No tax analysis data to export')
      return
    }

    try {
      // Create CSV content
      const csvContent = generateTaxSummaryCSV(taxAnalysisResult, property)

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `tax-summary-${property.property_address}-${selectedYear}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showSuccess('Export Complete', 'Tax summary exported successfully')
    } catch (error) {
      console.error('Export error:', error)
      showError('Export Error', 'Failed to export tax summary')
    }
  }

  if (loading || isCalculating) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-gray-600">
            {isCalculating ? 'Calculating tax analysis...' : 'Loading...'}
          </span>
        </div>
      </div>
    )
  }

  if (!taxProfile) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Tax Profile Required</h3>
          <p className="text-gray-600 mb-4">
            Please configure your tax profile to view detailed tax analysis.
          </p>
          <Button onClick={() => {/* TODO: Navigate to tax profile setup */}}>
            Set Up Tax Profile
          </Button>
        </div>
      </div>
    )
  }

  if (!taxAnalysisResult) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <Calculator className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Calculating Tax Analysis</h3>
          <p className="text-gray-600">Please wait while we calculate your tax implications.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Annual Tax Summary - {selectedYear}
                </h3>
                <p className="text-sm text-gray-600">
                  Comprehensive tax analysis for {property.property_address}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={selectedYear}
                onChange={(e) => onYearChange?.(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {AVAILABLE_YEARS.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExportTaxSummary}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </Button>
            </div>
          </div>
        </div>

        {/* Key Metrics Summary */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600 uppercase tracking-wide">
                    Annual Rental Income
                  </p>
                  <p className="text-2xl font-bold text-blue-900">
                    ${taxAnalysisResult.rental_income.toLocaleString()}
                  </p>
                </div>
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
            </div>

            <div className="bg-red-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600 uppercase tracking-wide">
                    Total Deductions
                  </p>
                  <p className="text-2xl font-bold text-red-900">
                    ${taxAnalysisResult.total_deductions.toLocaleString()}
                  </p>
                </div>
                <TrendingDown className="w-5 h-5 text-red-600" />
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600 uppercase tracking-wide">
                    Tax Savings
                  </p>
                  <p className="text-2xl font-bold text-green-900">
                    ${taxAnalysisResult.tax_savings.toLocaleString()}
                  </p>
                </div>
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600 uppercase tracking-wide">
                    After-Tax Cash Flow
                  </p>
                  <p className={`text-2xl font-bold ${taxAnalysisResult.after_tax_cash_flow >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                    ${taxAnalysisResult.after_tax_cash_flow.toLocaleString()}
                  </p>
                </div>
                <Calculator className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Deductions Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5" />
            Deductions Breakdown
          </h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie
                  data={deductionsData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="amount"
                  label={(props: any) => props.category && props.percentage ? `${props.category}: ${props.percentage}%` : ''}
                >
                  {deductionsData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={EXPENSE_COLORS[index % EXPENSE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => [`$${value.toLocaleString()}`, 'Amount']} />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Deductions Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Deduction Details</h4>
          <div className="space-y-3">
            {deductionsData.map((item, index) => (
              <div key={item.category} className="flex items-center justify-between py-2 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: EXPENSE_COLORS[index % EXPENSE_COLORS.length] }}
                  />
                  <span className="text-sm font-medium text-gray-700">{item.category}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-gray-900">
                    ${item.amount.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500">
                    {item.percentage}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Year-over-Year Trend */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Tax Impact Trend</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={yearOverYearData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip formatter={(value: number) => [`$${value.toLocaleString()}`, 'Amount']} />
              <Legend />
              <Bar dataKey="rental_income" fill="#3B82F6" name="Rental Income" />
              <Bar dataKey="tax_savings" fill="#10B981" name="Tax Savings" />
              <Bar dataKey="after_tax_cash_flow" fill="#8B5CF6" name="After-Tax Cash Flow" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tax Considerations */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-500 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-amber-800 mb-2">Important Tax Considerations</h4>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>• Depreciation recapture tax of ~${taxAnalysisResult.depreciation_recapture_liability.toLocaleString()} may apply upon sale</li>
              <li>• Passive activity loss rules may limit deductions if AGI exceeds $150,000</li>
              <li>• These calculations are estimates - consult a tax professional for specific advice</li>
              <li>• Keep detailed records of all expenses and improvements for tax purposes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper functions
function calculateMonthlyPayment(loanAmount: number, annualInterestRate: number, loanTermYears: number): number {
  const monthlyInterestRate = annualInterestRate / 100 / 12
  const numberOfPayments = loanTermYears * 12

  if (monthlyInterestRate === 0) {
    return loanAmount / numberOfPayments
  }

  const monthlyPayment = loanAmount *
    (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) /
    (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1)

  return Math.round(monthlyPayment)
}

function generateTaxSummaryCSV(analysis: TaxAnalysisResult, property: PropertyResponse): string {
  const headers = [
    'Property Address',
    'Analysis Year',
    'Rental Income',
    'Total Deductions',
    'Net Rental Income',
    'Taxable Income',
    'Tax Savings',
    'Effective Tax Rate',
    'After-Tax Cash Flow'
  ]

  const data = [
    property.property_address,
    analysis.analysis_year,
    analysis.rental_income.toFixed(2),
    analysis.total_deductions.toFixed(2),
    analysis.net_rental_income.toFixed(2),
    analysis.taxable_income.toFixed(2),
    analysis.tax_savings.toFixed(2),
    (analysis.effective_tax_rate * 100).toFixed(2) + '%',
    analysis.after_tax_cash_flow.toFixed(2)
  ]

  return [headers.join(','), data.join(',')].join('\n')
}